import { useQuery, gql, NetworkStatus } from '@apollo/client'
import { useEffect } from 'react'
import { FormattedMessage, useIntl, defineMessages } from 'react-intl'
import { useNavigate, useLocation } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import { Column } from '@10xLabs/react-web/components/Column'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Row } from '@10xLabs/react-web/components/Row'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { SitesSalesHeader } from 'features/SitesSales/components/Header'
import { SitesSalesTabs } from 'features/SitesSales/components/Tabs'
import { SitesSalesContent } from 'features/SitesSales/components/Content'
import {
  Filters,
  FiltersWrapper,
  getTimeframeDates,
  TIMEFRAMES_LIST,
  TIMEFRAMES
} from 'features/SitesSales/components/Filters'
import { DashboardSummary } from './Summary'
import { TopAgents } from './TopAgents'
import { TopCompanies } from './TopCompanies'
import { TopServices } from './TopServices'
import { TopSites } from './TopSites'
import { getTimezone } from 'utils/date'
import { getQueryString, getQueries } from 'utils/route'
import { hasAllAccess } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { useCompanySuggestionsByIdsQuery } from 'features/Companies/hooks'
import { isEmpty } from 'lodash'
import { GTCompanySuggestion } from 'gql/types.generated'

const messages = defineMessages({
  helmet: {
    id: 'drawers.helmet',
    defaultMessage: 'PoS Dashboard | Nexbus Admin'
  }
})

const GET_SITES_DASHBOARD = gql`
  query GetSitesDashboard($companyIds: [ID!], $siteIds: [ID!], $from: String!, $to: String!, $timezone: String!) {
    sitesDashboard(input: { companyIDs: $companyIds, siteIDs: $siteIds, from: $from, to: $to, timezone: $timezone }) {
      totalSales {
        cash {
          count
          amount
        }
        card {
          count
          amount
        }
        total {
          count
          amount
        }
      }
      topSites {
        id
        name
        company
        count
        amount
      }
      topCompanies {
        name
        count
        amount
      }
      topAgents {
        firstName
        lastName
        company
        amount
        count
      }
      topServices {
        code
        origin
        destination
        amount
        count
      }
    }
  }
`

const DashboardContent = ({ dashboard, filters }: TSFixMe): TSFixMe => {
  return (
    <div>
      <Row>
        <Column>
          <DashboardSummary sales={dashboard.totalSales} />
        </Column>
      </Row>

      <Row>
        <Column width="50%">
          <TopServices services={dashboard.topServices} />
        </Column>

        <Column width="50%">
          <TopSites sites={dashboard.topSites} filters={filters} />
        </Column>

        <Column width="50%">
          <TopAgents agents={dashboard.topAgents} />
        </Column>

        <Column width="50%">
          <TopCompanies companies={dashboard.topCompanies} />
        </Column>
      </Row>
    </div>
  )
}

const DashboardView = (): TSFixMe => {
  const location = useLocation()
  let content
  const intl = useIntl()
  const navigate = useNavigate()
  const login = useLogin()

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  const { timeframe, companyIds = '', siteIds = '' } = getQueries(location.search)
  const sanitizedTimeframe = TIMEFRAMES_LIST.indexOf(timeframe) > -1 ? timeframe : TIMEFRAMES.SEVEN_DAYS
  const companyIdsList = companyIds ? companyIds.split(',') : []
  const sanitizedSiteIds = siteIds ? siteIds.split(',') : []
  const { from, to } = getTimeframeDates(sanitizedTimeframe)

  const { companies } = useCompanySuggestionsByIdsQuery({
    ids: companyIdsList,
    skip: isEmpty(companyIdsList)
  })
  const { data, error, loading, networkStatus, refetch } = useQuery(GET_SITES_DASHBOARD, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      companyIds: companyIdsList,
      siteIds: sanitizedSiteIds,
      from,
      to,
      timezone: getTimezone()
    }
  })

  useEffect((): TSFixMe => {
    refetch()
  }, [timeframe])

  const filters = {
    companies: !isEmpty(companies)
      ? companies
      : companyIdsList.map((id: string): GTCompanySuggestion => ({ id, name: '', vertical: '' })),
    siteIds: sanitizedSiteIds,
    timeframe: sanitizedTimeframe
  }

  const applyFilters = (values: TSFixMe): TSFixMe => {
    const query = {
      companyIds: values.companies.map((company: GTCompanySuggestion): string => company.id).join(','),
      siteIds: values.siteIds,
      timeframe: values.timeframe
    }

    navigate(`/sites/sales?${getQueryString(query)}`)
  }

  if (!hasAllAccess(FEATURES.SITE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.SITE} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="sites.dashboard.error.description"
            defaultMessage="There was an error while loading your dashboard"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="sites.dashboard.error.action" defaultMessage="Reload your dashboard" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = <DashboardContent dashboard={data.sitesDashboard} filters={filters} />
  }

  return (
    <div>
      <SitesSalesHeader />

      <FiltersWrapper>
        <Filters filters={filters} applyFilters={applyFilters} />
      </FiltersWrapper>

      <SitesSalesTabs />

      <SitesSalesContent>{content}</SitesSalesContent>
    </div>
  )
}

export default withLastUrl(DashboardView)
